import React, { useMemo, useState, useEffect } from "react";
import {
  FiClock,
  FiRefreshCw,
  FiTruck,
  FiCheckCircle,
  FiSearch,
  FiFilter,
  FiExternalLink,
  FiRefreshCw as FiSync,
  FiEye,
  FiDownload,
  FiExternalLink as FiTotalIcon,
} from "react-icons/fi";
import "../../Styles/Seller/Order.css";
import Modal from "../Modal/Modal";
import sellerApi from "../../utils/sellerApi";
import { showError, showSuccess } from "../../utils/Toast";
import { DEFAULT_PRODUCT_IMAGE, withDefaultProductImage } from "../../utils/productPresentation";
import {
  downloadSellerOrderInvoice,
  getInvoiceDownloadErrorMessage,
  isSellerOrderInvoiceDownloadAvailable,
} from "../../utils/orderInvoice";

function formatCSVValue(v) {
  if (v == null) return "";
  return `"${String(v).replace(/"/g, '""')}"`;
}

function capitalize(s) {
  if (!s && s !== 0) return "";
  const str = String(s);
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatStatusLabel(value) {
  if (!value) return "-";

  return String(value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatTrackingDateTime(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "-" : parsed.toLocaleString();
}

function lastN(str = "", n = 4) {
  const s = String(str || "");
  return s.slice(-n);
}

function normalizeMoney(value) {
  const parsed =
    typeof value === "string"
      ? Number(value.replace(/[^0-9.-]/g, ""))
      : Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
}

function resolveSellerOrderProductImage(image) {
  if (Array.isArray(image) && image.length > 0) {
    return withDefaultProductImage(image[0]);
  }

  const raw = String(image || "").trim();
  if (!raw) return DEFAULT_PRODUCT_IMAGE;

  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return withDefaultProductImage(parsed[0]);
      }
    } catch {
      return DEFAULT_PRODUCT_IMAGE;
    }
  }

  return withDefaultProductImage(raw);
}

const PACKAGE_IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const PACKAGE_IMAGE_ACCEPT = ".jpg,.jpeg,.png,.webp";
const PACKAGE_IMAGE_ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);
const PACKAGE_IMAGE_ALLOWED_EXTENSIONS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
]);

function getOrderItemId(order) {
  return (
    order?.raw?.first_order_item_id ||
    order?.raw?.order_item_id ||
    order?.raw?.item?.order_item_id ||
    order?.items?.order_item_id ||
    null
  );
}

function mapIncomingOrders(allorderdata) {
  if (!allorderdata) {
    return [];
  }

  const rawArray = Array.isArray(allorderdata)
    ? allorderdata
    : Object.values(allorderdata);

  return rawArray.map((item) => {
    const orderNumber =
      item.order_number ||
      item.orderNo ||
      item.order_number_string ||
      item.id ||
      `ORD-${item.id || Math.random().toString(36).slice(2, 8)}`;
    const itemInfo =
      item.item || {
        order_item_id: item.first_order_item_id || item.order_item_id,
        quantity: item.seller_item_count || item.quantity || 1,
      };
    const quality = itemInfo.quantity || 1;
    const baseProduct = item.product || {};
    const product = {
      ...baseProduct,
      id: baseProduct.id || item.first_product_id || item.product_id,
      name:
        baseProduct.name ||
        item.first_product_name ||
        item.product_name ||
        `${item.seller_item_count || 0} item(s)`,
      image_url: resolveSellerOrderProductImage(
        baseProduct.image_url ||
          baseProduct.singleImage ||
          item.first_product_image ||
          item.product_image ||
          ""
      ),
    };
    const order_id = item.order_id;

    const rawStatus = String(
      item.item_statuses ||
        item.item_status ||
        item.seller_fulfillment_status ||
        item.order_status ||
        item.status ||
        "placed"
    ).split(",")[0];
    const status = capitalize(
      rawStatus.toLowerCase ? rawStatus.toLowerCase() : rawStatus
    );

    const userId =
      item.user_id || (item.user && item.user.id) || item.customer_id || "";
    const username =
      item.username ||
      (item.user && (item.user.username || item.user.name)) ||
      item.customer_name ||
      (userId ? `User ${userId}` : "Unknown");

    const fallbackQuantity = Number(
      itemInfo.quantity || item.quantity || item.seller_item_count || 1
    );
    const fallbackUnitPrice = Number(
      item.unit_price ?? itemInfo.unit_price ?? 0
    );
    const fallbackDiscount = Number(
      item.discount ?? itemInfo.discount ?? 0
    );
    const computedAmount =
      (Number.isFinite(fallbackUnitPrice) ? fallbackUnitPrice : 0) -
      (Number.isFinite(fallbackDiscount) ? fallbackDiscount : 0);

    const totalAmount = normalizeMoney(
      item.seller_subtotal ??
        item.total_amount ??
        item.item_total ??
        item.amount ??
        item.total ??
        computedAmount * Math.max(1, Number.isFinite(fallbackQuantity) ? fallbackQuantity : 1)
    );

    const createdAt =
      item.created_at ||
      item.createdAt ||
      item.date ||
      item.updated_at ||
      new Date().toISOString();
    const date = new Date(createdAt).toISOString().slice(0, 10);

    const note = (item.metadata && item.metadata.note) || item.note || "";
    const last4digits = lastN(orderNumber, 4);

    return {
      id: orderNumber,
      order_number: orderNumber,
      zoho_invoice_available: item.zoho_invoice_available,
      zoho_invoice_number: item.zoho_invoice_number || null,
      zoho_invoice_status: item.zoho_invoice_status || null,
      seller_invoice_download_available: item.seller_invoice_download_available,
      seller_invoice_ready_to_ship: item.seller_invoice_ready_to_ship,
      last4digits,
      customer: username,
      user_id: userId,
      avatarUrl: `https://i.pravatar.cc/36?u=${userId}`,
      amount: `AED ${totalAmount}`,
      rawAmount: totalAmount,
      status,
      date,
      note,
      raw: item,
      items: itemInfo,
      quality,
      product,
      order_id,
    };
  });
}

function getSellerInvoiceOrder(order) {
  if (!order) return null;

  return {
    ...order,
    ...(order.detail || {}),
    order_id: order.order_id || order.detail?.order_id || order.detail?.id,
    order_number: order.order_number || order.detail?.order_number,
  };
}

function SellerDownloadInvoiceButton({
  order,
  variant = "table",
  downloadingOrderId,
  onDownload,
}) {
  const invoiceOrder = getSellerInvoiceOrder(order);

  if (!isSellerOrderInvoiceDownloadAvailable(invoiceOrder)) {
    return null;
  }

  const orderId = Number(invoiceOrder?.order_id || invoiceOrder?.id || 0);
  const downloading = Number(downloadingOrderId || 0) === orderId;
  const isTable = variant === "table";
  const label = downloading ? "Downloading..." : "Download Invoice";

  return (
    <button
      type="button"
      className={
        isTable
          ? `ord-action-btn ord-action-invoice${downloading ? " ord-action-invoice-loading" : ""}`
          : "ord-btn ord-btn-primary ord-invoice-popup-btn"
      }
      onClick={() => onDownload(invoiceOrder)}
      disabled={downloading}
      title={label}
      aria-label={label}
    >
      <FiDownload />
      {!isTable || downloading ? <span>{label}</span> : null}
    </button>
  );
}

function revokePreviewItems(items = []) {
  items.forEach((item) => {
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  });
}

export default function Order({
  totalorder = null,
  pendingorder = null,
  processingorder = null,
  shippedorder = null,
  deliveredorder = null,
  allorderdata = null,
  updateorderstatusRequest,
}) {
  // console.log("all", allorderdata);
  // console.log(allorderdata);
  const initialStats = [
    {
      label: "Pending Orders",
      key: "Pending",
      icon: <FiClock className="ord-stat-icon" />,
      colorClass: "ord-stat-orange",
    },
    {
      label: "Processing",
      key: "Processing",
      icon: <FiRefreshCw className="ord-stat-icon" />,
      colorClass: "ord-stat-blue",
    },
    {
      label: "Shipped",
      key: "Shipped",
      icon: <FiTruck className="ord-stat-icon" />,
      colorClass: "ord-stat-green",
    },
    {
      label: "Delivered",
      key: "Delivered",
      icon: <FiCheckCircle className="ord-stat-icon" />,
      colorClass: "ord-stat-purple",
    },
  ];

  // orders: converted from allorderdata
  const [orders, setOrders] = useState([]);

  // stats card state
  const [stats, setStats] = useState(
    initialStats.map((s) => ({ ...s, value: 0 }))
  );

  // filters / UI state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // modal state
  const [viewingOrder, setViewingOrder] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [messagingOrder, setMessagingOrder] = useState(null);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [trackingOrderId, setTrackingOrderId] = useState(null);
  const [isReadyToShipOpen, setIsReadyToShipOpen] = useState(false);
  const [readyToShipOrder, setReadyToShipOrder] = useState(null);
  const [readyToShipFiles, setReadyToShipFiles] = useState([]);
  const [readyToShipError, setReadyToShipError] = useState("");
  const [readyToShipSubmitting, setReadyToShipSubmitting] = useState(false);
  const [invoiceDownloadingOrderId, setInvoiceDownloadingOrderId] = useState(null);
  const [handledNotificationOrderId, setHandledNotificationOrderId] = useState(null);

  /* -----------------------------
     Map incoming allorderdata -> local orders
     ----------------------------- */
  useEffect(() => {
    setOrders(mapIncomingOrders(allorderdata));
  }, [allorderdata]);

  /* --- Derived / filtered orders --- */
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      // search across order id, last4digits, customer, and amount
      const q = search.trim().toLowerCase();
      if (q) {
        const match = [
          o.order_number,
          o.last4digits,
          o.customer,
          o.amount,
        ].some((field) =>
          String(field || "")
            .toLowerCase()
            .includes(q)
        );
        if (!match) return false;
      }

      // status filter
      if (statusFilter && statusFilter !== "All" && o.status !== statusFilter)
        return false;

      // date range
      if (startDate) {
        if (new Date(o.date) < new Date(startDate)) return false;
      }
      if (endDate) {
        const oDate = new Date(o.date).setHours(0, 0, 0, 0);
        const eDate = new Date(endDate).setHours(0, 0, 0, 0);
        if (oDate > eDate) return false;
      }

      return true;
    });
  }, [orders, search, statusFilter, startDate, endDate]);

  // console.log("filteredOrders", filteredOrders);

  /* --- Helpers: stats calculation --- */
  function recalcStats(nextOrders = orders) {
    const counts = {};
    nextOrders.forEach((o) => {
      // statuses are normalized already (Capitalized)
      counts[o.status] = (counts[o.status] || 0) + 1;
    });

    const next = initialStats.map((s) => {
      // prefer prop override (if provided), otherwise use computed count
      let override;
      if (s.key === "Pending") override = pendingorder;
      else if (s.key === "Processing") override = processingorder;
      else if (s.key === "Shipped") override = shippedorder;
      else if (s.key === "Delivered") override = deliveredorder;

      const value =
        override !== null && override !== undefined
          ? Number(override)
          : counts[s.key] || 0;

      return { ...s, value };
    });

    setStats(next);
  }

  // total uses totalorder prop else fallback to orders.length
  const totalValue =
    totalorder !== null && totalorder !== undefined
      ? Number(totalorder)
      : orders.length;

  // recalc whenever orders or count props change
  useEffect(() => {
    recalcStats(orders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, pendingorder, processingorder, shippedorder, deliveredorder]);

  /* --- Actions --- */

  function handleSearchChange(e) {
    setSearch(e.target.value);
  }
  function handleStatusFilterChange(e) {
    setStatusFilter(e.target.value);
  }
  function handleDateChange(which, value) {
    if (which === "start") setStartDate(value);
    else setEndDate(value);
  }
  // const STATUS_MAP = {
  //   Pending: "pending",
  //   Processing: "processing",
  //   Shipped: "dispatched",
  //   Delivered: "delivered",
  //   Returned: "returned",
  // };
  const STATUS_FLOW = {
    pending: ["accepted", "processing", "cancelled", "unavailable"],
    placed: ["accepted", "processing", "cancelled", "unavailable"],
    accepted: ["processing", "packed", "cancelled", "unavailable"],
    processing: ["packed", "ready_to_ship", "cancelled", "unavailable"],
    ready_to_ship: [],
    shipment_created: [],
    picked_up: [],
    packed: ["ready_to_ship"],
    dispatched: [],
    in_transit: [],
    out_for_delivery: [],
    delivered: ["returned"],
    unavailable: [],
    cancelled: [],
    returned: [],
  };
  const STATUS_LABELS = {
    pending: "Pending",
    placed: "Placed",
    accepted: "Accepted",
    processing: "Processing",
    packed: "Packed",
    ready_to_ship: "Ready to Ship",
    shipment_created: "Shipment Created",
    picked_up: "Picked Up",
    dispatched: "Dispatched",
    in_transit: "In Transit",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    unavailable: "Unavailable",
    cancelled: "Cancelled",
    returned: "Returned",
  };

  function getNextStatusOptions(currentStatus) {
    if (!currentStatus) return [];

    const backendStatus = currentStatus.toLowerCase().replace(/ /g, "_");
    return STATUS_FLOW[backendStatus] || [];
  }

  async function handleExportCSV() {
    try {
      const rows = filteredOrders;
      if (!rows.length) {
        alert("No orders to export with the current filters.");
        return;
      }
      const header = [
        "Order ID",
        "Customer",
        "Amount (AED)",
        "Status",
        "Date",
        "Note",
      ];
      const csv = [header.join(",")];
      rows.forEach((r) => {
        const line = [
          formatCSVValue(r.order_number),
          formatCSVValue(r.customer),
          formatCSVValue(r.rawAmount),
          formatCSVValue(r.status),
          formatCSVValue(r.date),
          formatCSVValue(r.note || ""),
        ].join(",");
        csv.push(line);
      });
      const csvContent = csv.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error", err);
      alert("Failed to export orders. Check console for details.");
    }
  }

  async function handleSyncOrders() {
    try {
      const confirmed = window.confirm(
        "Sync orders from server? This will replace local list in this demo."
      );
      if (!confirmed) return;
      window.location.reload();
      // In production replace this with API fetch to re-load all orders into parent and pass via allorderdata
      // alert("Demo sync: replace with real fetch and pass `allorderdata` prop.");
    } catch (err) {
      console.error(err);
      alert("Failed to sync orders. See console.");
    }
  }

  async function handleViewOrder(o) {
    try {
      const [response, shipmentResponse] = await Promise.all([
        sellerApi.get(`seller/orders/${o.order_id}`),
        sellerApi.get(`seller/orders/${o.order_id}/shipment`).catch(() => ({ data: { data: null } })),
      ]);
      setViewingOrder({
        ...o,
        detail: {
          ...(response.data?.data || {}),
          shipment: shipmentResponse.data?.data || null,
        },
      });
    } catch {
      setViewingOrder(o);
    }
    setIsViewOpen(true);
  }

  useEffect(() => {
    const targetOrderId = Number(
      new URLSearchParams(window.location.search).get("orderId") || 0
    );

    if (!targetOrderId || handledNotificationOrderId === targetOrderId || !orders.length) {
      return;
    }

    const targetOrder = orders.find(
      (order) => Number(order.order_id || order.raw?.order_id || 0) === targetOrderId
    );

    if (!targetOrder) return;

    setHandledNotificationOrderId(targetOrderId);
    void handleViewOrder(targetOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, handledNotificationOrderId]);

  async function refreshOrders() {
    try {
      const response = await sellerApi.get("seller/orders");
      setOrders(mapIncomingOrders(response.data?.data || []));
    } catch (error) {
      showError(
        error.response?.data?.message || "Unable to refresh seller orders."
      );
    }
  }

  async function handleTrackShipment(order) {
    try {
      setTrackingOrderId(order.order_id);
      const response = await sellerApi.get(`seller/orders/${order.order_id}/shipment/track`);
      setViewingOrder((current) => ({
        ...current,
        detail: {
          ...(current?.detail || {}),
          shipment: {
            ...(current?.detail?.shipment || {}),
            tracking_response: response.data?.data,
            shipment_status: response.data?.data?.status || current?.detail?.shipment?.shipment_status,
            events: response.data?.data?.events || current?.detail?.shipment?.events || [],
          },
        },
      }));
      showSuccess(
        response.data?.message || "Shipment tracking fetched successfully."
      );
    } catch (error) {
      showError(
        error.response?.data?.message ||
          "Unable to fetch shipment tracking at the moment."
      );
    } finally {
      setTrackingOrderId(null);
    }
  }

  async function handleSellerInvoiceDownload(order) {
    const orderId = Number(order?.order_id || order?.id || 0);

    if (!orderId || Number(invoiceDownloadingOrderId || 0) === orderId) {
      return;
    }

    try {
      setInvoiceDownloadingOrderId(orderId);
      await downloadSellerOrderInvoice(order);
      showSuccess("Invoice downloaded successfully.");
    } catch (error) {
      showError(await getInvoiceDownloadErrorMessage(error));
    } finally {
      setInvoiceDownloadingOrderId(null);
    }
  }

  function closeView() {
    setViewingOrder(null);
    setIsViewOpen(false);
  }

  // function handleShipOrDeliver(order) {
  //   // update local status only (demo). Expect backend call here.
  //   // console.log(order);
  //   let nextStatus;
  //   if (order.status === "Processing") nextStatus = "Shipped";
  //   else if (order.status === "Shipped") nextStatus = "Delivered";
  //   else nextStatus = "Shipped";

  //   // if (!window.confirm(`Mark ${order.order_number} as ${nextStatus}?`)) return;

  //   const updated = orders.map((o) =>
  //     o.order_number === order.order_number ? { ...o, status: nextStatus } : o
  //   );
  //   setOrders(updated);
  //   const tracknumber = order.raw?.courier_tracking_number;
  //   const params = {
  //     courier_tracking_number: tracknumber || "98765432",
  //     // status: nextStatus
  //     orderId: order.order_number,
  //     new_status: nextStatus,
  //   };
  //   // console.log(params);

  //   // alert(`${order.order_number} marked ${nextStatus}.`);
  //   updateorderstatusRequest(params);
  //   // TODO: call API to persist change
  // }

  function resetReadyToShipModal() {
    revokePreviewItems(readyToShipFiles);
    setReadyToShipFiles([]);
    setReadyToShipError("");
    setReadyToShipOrder(null);
    setReadyToShipSubmitting(false);
    setIsReadyToShipOpen(false);
  }

  function openReadyToShipModal(order) {
    revokePreviewItems(readyToShipFiles);
    setReadyToShipFiles([]);
    setReadyToShipError("");
    setReadyToShipOrder(order);
    setIsReadyToShipOpen(true);
  }

  function validateSelectedImages(files) {
    if (files.length < 2) {
      return "Please upload at least 2 package photos.";
    }

    if (files.length > 4) {
      return "Maximum 4 package photos allowed.";
    }

    for (const file of files) {
      const fileName = String(file?.name || "");
      const extension = fileName.includes(".")
        ? `.${fileName.split(".").pop().toLowerCase()}`
        : "";

      if (
        !PACKAGE_IMAGE_ALLOWED_TYPES.has(String(file?.type || "").toLowerCase()) ||
        !PACKAGE_IMAGE_ALLOWED_EXTENSIONS.has(extension)
      ) {
        return "Only JPG, JPEG, PNG, and WEBP package photos are allowed.";
      }

      if (Number(file?.size || 0) > PACKAGE_IMAGE_MAX_SIZE) {
        return "Each package photo must be 5 MB or smaller.";
      }
    }

    return "";
  }

  function handleReadyToShipFilesChange(event) {
    const nextFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!nextFiles.length) return;

    setReadyToShipFiles((current) => {
      const existingKeys = new Set(
        current.map(
          (item) =>
            `${item.file.name}-${item.file.size}-${item.file.lastModified}`
        )
      );

      const uniqueIncoming = nextFiles.filter((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        return !existingKeys.has(key);
      });

      const combinedFiles = [
        ...current.map((item) => item.file),
        ...uniqueIncoming,
      ];
      const validationMessage = validateSelectedImages(combinedFiles);
      if (
        validationMessage &&
        validationMessage !== "Please upload at least 2 package photos."
      ) {
        setReadyToShipError(validationMessage);
        return current;
      }

      const nextPreviewItems = uniqueIncoming.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));

      setReadyToShipError(validationMessage);
      return [...current, ...nextPreviewItems];
    });
  }

  function removeReadyToShipFile(fileId) {
    setReadyToShipFiles((current) => {
      const target = current.find((item) => item.id === fileId);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }

      const nextFiles = current.filter((item) => item.id !== fileId);
      setReadyToShipError(
        validateSelectedImages(nextFiles.map((item) => item.file))
      );
      return nextFiles;
    });
  }

  async function submitReadyToShip() {
    if (!readyToShipOrder) return;

    const files = readyToShipFiles.map((item) => item.file);
    const validationMessage = validateSelectedImages(files);
    if (validationMessage) {
      setReadyToShipError(validationMessage);
      return;
    }

    const orderItemId = getOrderItemId(readyToShipOrder);
    if (!orderItemId) {
      setReadyToShipError(
        "Open order detail before updating this legacy order item."
      );
      return;
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("package_images", file);
    });

    try {
      setReadyToShipSubmitting(true);
      setReadyToShipError("");
      const response = await sellerApi.post(
        `seller/orders/${readyToShipOrder.order_id}/items/${orderItemId}/ready-to-ship`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      showSuccess(
        response.data?.message || "Order item marked Ready To Ship successfully"
      );
      resetReadyToShipModal();
      await refreshOrders();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Unable to mark this order item Ready To Ship.";
      setReadyToShipError(message);
      showError(message);
      setReadyToShipSubmitting(false);
    }
  }

  function handleStatusChange(order, selectedStatus) {
    const backendStatus = selectedStatus;

    if (!backendStatus) {
      alert("Invalid status selected");
      return;
    }

    const orderItemId = getOrderItemId(order);

    if (!orderItemId) {
      alert("Open order detail before updating this legacy order item.");
      return;
    }

    if (backendStatus === "ready_to_ship") {
      openReadyToShipModal(order);
      return;
    }

    const params = {
      orderId: order.order_id,
      orderItemId,
      new_status: backendStatus,
      courier_tracking_number: order.raw?.courier_tracking_number || null,
      note: `Status updated to ${backendStatus}`,
    };

    // optimistic UI update (keeps UI fast)
    setOrders((prev) =>
      prev.map((o) =>
        o.id === order.id ? { ...o, status: selectedStatus } : o
      )
    );
    updateorderstatusRequest(params);
  }

  function openMessage(order) {
    setMessagingOrder(order);
    setMessageText("");
    setIsMessageOpen(true);
  }
  function closeMessage() {
    setMessagingOrder(null);
    setMessageText("");
    setIsMessageOpen(false);
  }
  function handleSendMessage() {
    if (!messagingOrder) return;
    if (!messageText.trim()) {
      alert("Please write a message before sending.");
      return;
    }
    // demo only
    alert(`Message sent to ${messagingOrder.customer}:\n\n${messageText}`);
    closeMessage();
  }

  /* --- UI --- */
  function normalizeStatusClass(status) {
    if (!status) return "";
    return status.toLowerCase().replace(/\s+/g, "_");
  }

  return (
    <div className="ord-page">
      <div className="ord-header">
        <h1>Order Management</h1>

        <div className="ord-top-actions">
          <button
            className="ord-top-btn"
            onClick={() => {
              const input = document.querySelector(".ord-search-input");
              if (input) input.focus();
            }}
          >
            <FiFilter /> Filter
          </button>

          <button className="ord-top-btn" onClick={handleExportCSV}>
            <FiExternalLink /> Export Orders
          </button>

          <button className="ord-sync-btn" onClick={handleSyncOrders}>
            <FiSync /> Sync Orders
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="ord-stats">
        <div className="ord-stat-card ord-stat-total" key="total-orders">
          <div>
            <div className="ord-stat-label">Total Orders</div>
            <div className="ord-stat-value">{totalValue}</div>
          </div>
          <div className={`ord-stat-badge ord-stat-total-badge`}>
            <FiTotalIcon className="ord-stat-icon" />
          </div>
        </div>

        {stats.map((s) => (
          <div className="ord-stat-card" key={s.label}>
            <div>
              <div className="ord-stat-label">{s.label}</div>
              <div className="ord-stat-value">{s.value}</div>
            </div>
            <div className={`ord-stat-badge ${s.colorClass}`}>{s.icon}</div>
          </div>
        ))}
      </div>

      {/* Orders table */}
      <div className="ord-list-card">
        <div className="ord-filters-row">
          <div className="ord-search">
            <FiSearch className="ord-search-icon" />
            <input
              className="ord-search-input"
              placeholder="Search orders by ID, customer, or amount..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>

          <div className="ord-filters-right">
            <select
              className="ord-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Placed">Placed</option>
              <option value="Accepted">Accepted</option>
              <option value="Processing">Processing</option>
              <option value="Packed">Packed</option>
              <option value="Ready_to_ship">Ready to Ship</option>
              <option value="Shipped">Shipped</option>
              <option value="Delivered">Delivered</option>
              <option value="Cancelled">Cancelled</option>
            </select>

            <input
              className="ord-date"
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange("start", e.target.value)}
              aria-label="Start date"
            />
            <input
              className="ord-date"
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange("end", e.target.value)}
              aria-label="End date"
            />
          </div>
        </div>

        <div className="ord-table-container">
          <table className="ord-table-html">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Image</th>
                <th>Product Name</th>
                <th>Customer</th>
                <th>Qty</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th className="ord-actions-col">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.map((o) => (
                <tr key={o.order_number}>
                  {/* Order ID */}
                  <td>#ORD {o.last4digits}</td>

                  {/* Product Image */}
                  <td>
                    <img
                      src={withDefaultProductImage(o.product?.image_url)}
                      alt="Product"
                      className="ord-product-img"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                      }}
                    />
                  </td>

                  {/* Product Name */}
                  <td className="ord-customer-name">{o.product?.name}</td>

                  {/* Customer */}
                  <td className="ord-customer-name">{o.customer}</td>

                  {/* Quantity */}
                  {/* {console.log(o)} */}
                  <td className="ord-qty">{o.quality || 1}</td>

                  {/* Amount */}
                  <td className="ord-amount-value">{o.amount}</td>

                  {/* Status */}
                  <td>
                    <div className="ord-status-container">
                      <span
                        className={`ord-status-indicator ord-status-${normalizeStatusClass(
                          o.status
                        )}`}
                      />

                      <span className="ord-status-text">{o.status}</span>
                    </div>
                  </td>

                  {/* Date */}
                  <td className="ord-date-value">{o.date}</td>

                  {/* Actions */}
                  <td>
                    <div className="ord-action-buttons">
                      <button
                        className="ord-action-btn ord-action-view"
                        onClick={() => handleViewOrder(o)}
                      >
                        <FiEye />
                      </button>

                      <SellerDownloadInvoiceButton
                        order={o}
                        variant="table"
                        downloadingOrderId={invoiceDownloadingOrderId}
                        onDownload={handleSellerInvoiceDownload}
                      />

                      <select
                        className="ord-status-table-select"
                        value=""
                        onChange={(e) => handleStatusChange(o, e.target.value)}
                        disabled={
                          getNextStatusOptions(o.status).length === 0 ||
                          !(
                            o.raw?.first_order_item_id ||
                            o.raw?.order_item_id ||
                            o.raw?.item?.order_item_id ||
                            o.items?.order_item_id
                          )
                        }
                      >
                        <option value="" disabled>
                          Change Status
                        </option>

                        {getNextStatusOptions(o.status).map((nextStatus) => (
                          <option key={nextStatus} value={nextStatus}>
                            {STATUS_LABELS[nextStatus]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* View modal */}
      <Modal isOpen={isViewOpen} onClose={closeView} maxWidth="700px">
        {viewingOrder && (
          <div style={{ padding: 12 }}>
            <h3 style={{ marginTop: 0 }}>
              Order details — {viewingOrder.order_number}
            </h3>
            <div style={{ marginBottom: 12 }}>
              <p>
                <strong>Customer:</strong> {viewingOrder.customer}
              </p>
              <p>
                <strong>Amount:</strong> {viewingOrder.amount}
              </p>
              <p>
                <strong>Status:</strong> {viewingOrder.status}
              </p>
              <p>
                <strong>Date:</strong> {viewingOrder.date}
              </p>
              {viewingOrder.note && (
                <p>
                  <strong>Note:</strong> {viewingOrder.note}
                </p>
              )}
            </div>

            {viewingOrder.detail?.items?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <h4>Items</h4>
                {viewingOrder.detail.items.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      borderTop: "1px solid #eee",
                      padding: "8px 0",
                    }}
                  >
                    <span>
                      {item.product_snapshot?.name ||
                        item.product_name ||
                        `Product #${item.product_id}`}
                    </span>
                    <span>Qty: {item.quantity}</span>
                    <span>{String(item.item_status || "placed").replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            )}

            {viewingOrder.detail?.shipment && (
              <div style={{ marginBottom: 12 }}>
                <h4>Shipment</h4>
                <p>
                  <strong>Partner:</strong>{" "}
                  {viewingOrder.detail.shipment.delivery_partner_name ||
                    viewingOrder.detail.shipment.delivery_partner_code ||
                    "EMX"}
                </p>
                <p>
                  <strong>AWB:</strong> {viewingOrder.detail.shipment.awb_number || "-"}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {formatStatusLabel(
                    viewingOrder.detail.shipment.tracking_response?.status ||
                      viewingOrder.detail.shipment.shipment_status ||
                      "shipment_created"
                  )}
                </p>
                <p>
                  <strong>EMX Last Status:</strong>{" "}
                  {formatStatusLabel(
                    viewingOrder.detail.shipment.last_tracking_status_description ||
                      viewingOrder.detail.shipment.tracking_response?.last_status_description ||
                      viewingOrder.detail.shipment.tracking_response?.status ||
                      "-"
                  )}
                </p>
                <p>
                  <strong>Last Updated:</strong>{" "}
                  {formatTrackingDateTime(
                    viewingOrder.detail.shipment.last_tracked_at ||
                      viewingOrder.detail.shipment.tracking_response?.last_tracked_at ||
                      viewingOrder.detail.shipment.events?.[0]?.event_time ||
                      viewingOrder.detail.shipment.tracking_response?.events?.[0]?.event_time ||
                      null
                  )}
                </p>
                {viewingOrder.detail.shipment.awb_number && (
                  <button
                    className="ord-btn ord-btn-primary"
                    onClick={() => handleTrackShipment(viewingOrder)}
                    disabled={trackingOrderId === viewingOrder.order_id}
                  >
                    {trackingOrderId === viewingOrder.order_id ? "Tracking..." : "Track Shipment"}
                  </button>
                )}
                {(viewingOrder.detail.shipment.events ||
                  viewingOrder.detail.shipment.tracking_response?.events ||
                  []).map((event, index) => (
                  <div
                    key={event.id || index}
                    style={{
                      borderTop: "1px solid #eee",
                      padding: "8px 0",
                    }}
                  >
                    <strong>
                      {formatStatusLabel(
                        event.status_description || event.status || "shipment update"
                      )}
                    </strong>
                    <div>{event.description || ""}</div>
                  </div>
                ))}
              </div>
            )}

            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <SellerDownloadInvoiceButton
                order={viewingOrder}
                variant="popup"
                downloadingOrderId={invoiceDownloadingOrderId}
                onDownload={handleSellerInvoiceDownload}
              />
              <button className="ord-btn" onClick={closeView}>
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isReadyToShipOpen}
        onClose={() => {
          if (!readyToShipSubmitting) {
            resetReadyToShipModal();
          }
        }}
        maxWidth="720px"
      >
        {readyToShipOrder && (
          <div style={{ padding: 12 }}>
            <h3 style={{ marginTop: 0 }}>
              Ready To Ship - Upload Package Photos
            </h3>

            <div className="ord-upload-meta">
              <p>
                <strong>Order number:</strong> {readyToShipOrder.order_number}
              </p>
              <p>
                <strong>Product:</strong>{" "}
                {readyToShipOrder.product?.name ||
                  `${readyToShipOrder.quality || 1} item(s)`}
              </p>
              <p className="ord-upload-instruction">
                Upload 2 to 4 packed product photos before marking ready to ship.
              </p>
            </div>

            <div className="ord-upload-panel">
              <label className="ord-upload-label" htmlFor="ready-to-ship-upload">
                Select package photos
              </label>
              <input
                id="ready-to-ship-upload"
                type="file"
                accept={PACKAGE_IMAGE_ACCEPT}
                multiple
                onChange={handleReadyToShipFilesChange}
                disabled={readyToShipSubmitting}
              />
              <div className="ord-upload-hint">
                Accepted formats: JPG, JPEG, PNG, WEBP. Minimum 2, maximum 4 photos. Max 5 MB each.
              </div>

              {readyToShipError ? (
                <div className="ord-upload-error">{readyToShipError}</div>
              ) : null}

              {readyToShipFiles.length ? (
                <div className="ord-upload-preview-grid">
                  {readyToShipFiles.map((item) => (
                    <div key={item.id} className="ord-upload-preview-card">
                      <img
                        src={item.previewUrl}
                        alt={item.file.name}
                        className="ord-upload-preview-image"
                      />
                      <div className="ord-upload-preview-name" title={item.file.name}>
                        {item.file.name}
                      </div>
                      <button
                        type="button"
                        className="ord-upload-remove-btn"
                        onClick={() => removeReadyToShipFile(item.id)}
                        disabled={readyToShipSubmitting}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ord-upload-empty">
                  No package photos selected yet.
                </div>
              )}
            </div>

            <div className="ord-upload-actions">
              <button
                type="button"
                className="ord-upload-secondary-btn"
                onClick={resetReadyToShipModal}
                disabled={readyToShipSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ord-upload-primary-btn"
                onClick={submitReadyToShip}
                disabled={readyToShipSubmitting}
              >
                {readyToShipSubmitting
                  ? "Marking Ready To Ship..."
                  : "Mark Ready To Ship"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Message modal */}
      <Modal isOpen={isMessageOpen} onClose={closeMessage} maxWidth="700px">
        {messagingOrder && (
          <div style={{ padding: 12 }}>
            <h3 style={{ marginTop: 0 }}>
              Message — {messagingOrder.customer} ({messagingOrder.order_number}
              )
            </h3>
            <div style={{ marginBottom: 12 }}>
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Write a message to the customer..."
                rows={8}
                style={{ width: "100%", padding: 8, fontFamily: "inherit" }}
              />
            </div>

            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button className="ord-btn" onClick={closeMessage}>
                Cancel
              </button>
              <button
                className="ord-btn ord-btn-primary"
                onClick={handleSendMessage}
              >
                Send
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
